import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import DeleteConfirmModal from 'components/DeleteConfirmModal';
import FilmIncentiveEditorModal from 'components/FilmIncentiveEditorModal';
import { useFilmIncentives } from 'hooks/useFilmIncentives';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useTagQuery } from 'utils/api/queries';
import { formatCurrency, formatPercentage } from 'utils/helper';
import { ArrowBackLong, Icon } from 'utils/icons';

import './styles.scss';

const getStatus = incentive => {
  if (incentive.missing_exchange_rate) {
    return {
      label: 'Incomplete',
      detail: 'Missing exchange rate',
      tone: 'warning',
    };
  }
  if (!incentive.configuration_complete) {
    return {
      label: 'Incomplete',
      detail: 'Qualified spend is not configured',
      tone: 'warning',
    };
  }
  if (!incentive.threshold_met) {
    return {
      label: 'Below minimum',
      detail: 'The minimum qualified spend has not been reached',
      tone: 'warning',
    };
  }
  return { label: 'Estimated', detail: 'Ready to include', tone: 'success' };
};

const getQualifiedSpendSource = incentive => {
  if (incentive.calculation_source === 'manual') return 'Manual amount';
  const names = (incentive.eligible_tags || []).map(tag => tag.name);
  return names.length ? names.join(', ') : 'No tags selected';
};

const CreditsDetails = () => {
  const navigate = useNavigate();
  const { budgetData, selectedBudget, hasEditAccess, refetchBudgetId } =
    useHomeScreenContext();
  const canEdit = hasEditAccess?.();
  const currency = budgetData?.primary_currency || 'USD';
  const budgetId = selectedBudget?.budget || budgetData?.id || null;
  const { incentives, isFetching, isSaving, saveIncentive, deleteIncentive } =
    useFilmIncentives(budgetId, Boolean(budgetId));
  const { data: tagsResponse } = useTagQuery.useTagsQuery({
    filters: { budget: budgetId },
    enabled: Boolean(budgetId),
  });
  const tags = useMemo(() => tagsResponse?.data || [], [tagsResponse]);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingIncentive, setEditingIncentive] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isLoadingCredits = Boolean(budgetId) && isFetching;

  const totalNetCredits = incentives.reduce(
    (total, incentive) =>
      total + (incentive.estimate_complete ? Number(incentive.net_credit) : 0),
    0
  );

  const closeEditor = () => {
    if (isSaving) return;
    setShowEditorModal(false);
    setEditingIncentive(null);
  };

  const openCreate = () => {
    setEditingIncentive(null);
    setShowEditorModal(true);
  };

  const openEdit = incentive => {
    setEditingIncentive(incentive);
    setShowEditorModal(true);
  };

  const save = async payload => {
    try {
      await saveIncentive(payload);
      toast.success(
        editingIncentive ? 'Credit updated.' : 'Credit estimate added.'
      );
      setShowEditorModal(false);
      setEditingIncentive(null);
      refetchBudgetId?.();
    } catch (error) {
      // The shared hook presents the API error to the user.
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteIncentive(deleteTarget.id);
      toast.success('Credit deleted.');
      setDeleteTarget(null);
      if (editingIncentive?.id === deleteTarget.id) {
        setShowEditorModal(false);
        setEditingIncentive(null);
      }
      refetchBudgetId?.();
    } catch (error) {
      // The shared hook presents the API error to the user.
    }
  };

  const pageTitle = `${
    selectedBudget?.full_name || selectedBudget?.name || 'Budget'
  } Credits`;

  return (
    <div className="credits-details-wrapper">
      <div className="credits-details-header">
        <div className="credits-details-heading">
          <button
            type="button"
            className="credits-back-button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackLong />
          </button>
          <div>
            <h1>{pageTitle}</h1>
            <p>
              Manage estimated film tax credits and the qualified spend used by
              each calculation.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary credits-add-button"
          onClick={openCreate}
          disabled={!canEdit || !budgetId}
        >
          <span>
            <Icon name="DollarSignIcon" />
            Add New Credit
          </span>
        </button>
      </div>

      <section className="credits-card">
        <div className="credits-table-wrapper">
          <table className="credits-table">
            <thead>
              <tr>
                <th>Credit</th>
                <th>Qualified Spend</th>
                <th>Rate</th>
                <th>Net Credit</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {!budgetId && (
                <tr>
                  <td colSpan={6} className="credits-empty-state">
                    Open a budget to manage its film tax credits.
                  </td>
                </tr>
              )}
              {isLoadingCredits && (
                <tr>
                  <td colSpan={6}>Loading credits...</td>
                </tr>
              )}
              {!isLoadingCredits &&
                Boolean(budgetId) &&
                incentives.map(incentive => {
                  const status = getStatus(incentive);
                  return (
                    <tr key={incentive.id}>
                      <td>
                        <span className="credits-name">{incentive.name}</span>
                        <span className="credits-source">
                          {getQualifiedSpendSource(incentive)}
                        </span>
                      </td>
                      <td>
                        {formatCurrency(
                          incentive.qualified_spend || 0,
                          currency
                        )}
                        <span className="credits-row-count">
                          {incentive.calculation_source === 'tags'
                            ? `${incentive.row_count || 0} tagged row${
                                Number(incentive.row_count) === 1 ? '' : 's'
                              }`
                            : 'manual'}
                        </span>
                      </td>
                      <td>{formatPercentage(incentive.rate_percent)}%</td>
                      <td className="credits-net-credit">
                        {incentive.estimate_complete
                          ? formatCurrency(incentive.net_credit || 0, currency)
                          : '—'}
                      </td>
                      <td>
                        <span
                          className={`credits-status credits-status--${status.tone}`}
                          title={status.detail}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td>
                        <div className="credits-row-actions">
                          <button
                            type="button"
                            onClick={() => openEdit(incentive)}
                            disabled={!canEdit}
                            aria-label={`Edit ${incentive.name}`}
                          >
                            <Icon name="PenToSquareIcon" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(incentive)}
                            disabled={!canEdit}
                            aria-label={`Delete ${incentive.name}`}
                          >
                            <Icon name="TrashCanIcon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {!isLoadingCredits &&
                Boolean(budgetId) &&
                incentives.length === 0 && (
                  <tr>
                    <td colSpan={6} className="credits-empty-state">
                      No film tax credit estimates have been added yet.
                    </td>
                  </tr>
                )}
            </tbody>
            {incentives.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={3}>Total Estimated Credits</td>
                  <td className="credits-net-credit">
                    {formatCurrency(totalNetCredits, currency)}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      <p className="credits-disclaimer">
        Grand Total remains unchanged; credits reduce the displayed Net
        Production Cost.
      </p>

      <DeleteConfirmModal
        showModal={Boolean(deleteTarget)}
        handleOnClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        confirmText="Are you sure you want to delete"
        itemName={deleteTarget?.name || 'this credit'}
        confirmLabel="Delete"
        disabled={isSaving}
      />

      <FilmIncentiveEditorModal
        show={showEditorModal}
        onHide={closeEditor}
        incentive={editingIncentive}
        budgetId={budgetId}
        tags={tags}
        onSave={save}
        onDelete={
          editingIncentive
            ? () => {
                setShowEditorModal(false);
                setDeleteTarget(editingIncentive);
              }
            : null
        }
        isSaving={isSaving}
      />
    </div>
  );
};

export default CreditsDetails;
